import { inject, Injectable, LOCALE_ID, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { AppConfig } from '../../../app.config';
import { urlJoin } from '../../../core/helpers/url.helper';
import { ContractResponse, DerogationResponse } from './contract-response.model';
import { mapProjectInformationCard } from '../../layout/recap-card/recap-card.mapper';
import { SaveLeadResponse } from './contract-action.model';
import { catchError, Observable, of, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationStore } from '../../../core/services/application-store.service';
import {
  contractResponseToDematDeliveryContractData,
  contractResponseToVehicleData
} from './delivery/delivery.component.mapper';
import { createHeadersWithGlobalUuid, RecapCard } from '@tribeauto/ngx-mobilite-material';
import { LogService } from '../../../core/services/log/log.service';

@Injectable({
  providedIn: 'root'
})
export class ContractActionService {
  private readonly locale = inject(LOCALE_ID);
  private readonly httpClient = inject(HttpClient);
  private readonly appConfig = inject(AppConfig);
  private readonly translateService = inject(TranslateService);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly logService = inject(LogService);

  private readonly url = urlJoin(this.appConfig.url, 'contract');
  lastFetchedRecapCardList: WritableSignal<RecapCard[]> = signal([]);
  lastFetchedRecapCardListError: WritableSignal<HttpErrorResponse | null> = signal(null);

  lastFetchedProjectInformationRecapCard: WritableSignal<RecapCard> = signal({} as RecapCard);
  lastFetchedProjectInformationRecapCardError: WritableSignal<HttpErrorResponse | null> = signal(null);

  lastFetchedProjectRvDerogation: WritableSignal<DerogationResponse | null> = signal(null);
  contractResidualValue: WritableSignal<string> = signal('');

  fetchContractRecapListByNumAuto(numAuto: string): Observable<ContractResponse | null> {
    const headers = createHeadersWithGlobalUuid(this.logService.sessionUuid());
    return this.httpClient.get<ContractResponse>(urlJoin(this.url, numAuto), { headers }).pipe(
      catchError(error => {
        this.lastFetchedRecapCardListError.set(error);
        return of(null);
      })
    );
  }

  fetchContractDerogationRecapByNumAuto(numAuto: string): Observable<ContractResponse | null> {
    const headers = createHeadersWithGlobalUuid(this.logService.sessionUuid());
    return this.httpClient.get<ContractResponse>(urlJoin(this.url, numAuto), { headers }).pipe(
      tap(response => {
        this.contractResidualValue.set(String(response.offer.residualValueAmount ?? 0));
        this.lastFetchedProjectInformationRecapCard.set(
          mapProjectInformationCard(numAuto, response, this.locale, this.translateService)
        );
        this.lastFetchedProjectRvDerogation.set(response.rvDerogation);
      }),
      catchError(error => {
        this.contractResidualValue.set('');
        this.lastFetchedProjectInformationRecapCardError.set(error);
        this.lastFetchedProjectRvDerogation.set(null);
        return of(null);
      })
    );
  }

  updateStore(contract: ContractResponse): void {
    this.applicationStore.update('vehicleData', contractResponseToVehicleData(contract));
    this.applicationStore.update('signatureData', { signatureType: contract.signature?.signatureType });
    this.applicationStore.update(
      'dematDeliveryContractData',
      contractResponseToDematDeliveryContractData(contract, this.translateService)
    );
    this.applicationStore.update('webstoreData', { webstoreId: contract.pointOfSale.vendorNumber });
  }

  duplicateContract(numAuto: string): Observable<SaveLeadResponse> {
    return this.httpClient.get<SaveLeadResponse>(urlJoin(this.url, numAuto, 'duplicate'), {
      headers: createHeadersWithGlobalUuid(this.logService.sessionUuid())
    });
  }

  cancelContractBySeller(numAuto: string, reason: string): Observable<void> {
    const params = new HttpParams().set('reason', reason);
    return this.httpClient.put<void>(urlJoin(this.url, numAuto, 'cancel-by-seller'), null, {
      headers: createHeadersWithGlobalUuid(this.logService.sessionUuid()),
      params
    });
  }

  updateVehicleDeliveryDate(numAuto: string, deliveryDate: Date): Observable<void> {
    const headers = createHeadersWithGlobalUuid(this.logService.sessionUuid());
    return this.httpClient.put<void>(
      urlJoin(this.url, numAuto, 'dates', 'delivery-date'),
      { deliveryDate },
      { headers }
    );
  }
}
